import { hex2buf } from "../util";

function setUint32LE(bytes, offset, value) {
    bytes[offset] = value & 0xFF;
    bytes[offset + 1] = (value >>> 8) & 0xFF;
    bytes[offset + 2] = (value >>> 16) & 0xFF;
    bytes[offset + 3] = (value >>> 24) & 0xFF;
}

/**
 * Build a minimal, SFDP-rev-1.x-compliant blob for the requested flash
 * parameters. The resulting 256-byte Uint8Array can be parsed by SFDP.js
 * and will yield the correct densityBytes / addressBytes / pageSize and
 * the usual 4KB + 64KB erase types.
 *
 * `addressBytes: 4` encodes "4-byte only" (0b10). Pass `addressBytesField`
 * to set the raw BFPT DWORD1 bits 18:17 directly, e.g. 0b01 for the
 * "3- or 4-byte, default 3" that most real 256 Mbit parts report.
 *
 * `enter4ByteMethods` / `exit4ByteMethods` fill BFPT DWORD 16.
 * `fourByteTable: { read, program, eraseOpcodes: { type: opcode } }` adds
 * a 4-byte Address Instruction Table at `fourByteTablePointer` (which may
 * lie beyond the first 256 bytes, growing the blob).
 */
function buildMinimalSFDP({
    densityBytes,
    addressBytes = 3,
    addressBytesField = null,
    majorRev = 1,
    minorRev = 5,
    erase4kOpcode = 0x20,
    pageSize = 256,
    enter4ByteMethods = 0,
    exit4ByteMethods = 0,
    fourByteTable = null,
    fourByteTablePointer = 0x70,
}) {
    const densityBits = densityBytes * 8;
    let densityDword;
    if (densityBits <= 0x80000000) {
        // (N+1) bits encoding
        densityDword = (densityBits - 1) >>> 0;
    } else {
        // 2^N bits encoding (bit 31 set)
        densityDword = (0x80000000 | Math.log2(densityBits)) >>> 0;
    }

    let dword1 = 0;
    dword1 |= 0b01;                          // uniform 4K erase supported
    dword1 |= (erase4kOpcode & 0xFF) << 8;   // 4K erase opcode
    dword1 |= (addressBytesField ?? (addressBytes === 4 ? 0b10 : 0b00)) << 17;

    const pageSizeExp = Math.log2(pageSize) | 0;

    const bfptDwords = 16;
    const bfpt = new Uint8Array(bfptDwords * 4);
    setUint32LE(bfpt, 0, dword1 >>> 0);            // DWORD 1
    setUint32LE(bfpt, 4, densityDword);            // DWORD 2
    // DWORDs 3-7: zero
    setUint32LE(bfpt, 28, (erase4kOpcode << 8) | 12);  // DWORD 8: Erase Type 1 = 4KB
    setUint32LE(bfpt, 32, (0xD8 << 8) | 16);           // DWORD 9: Erase Type 3 = 64KB
    // DWORD 11 (bytes 40-43): bits 4-7 carry the page size exponent.
    setUint32LE(bfpt, 40, (pageSizeExp & 0x0F) << 4);
    // DWORD 16 (bytes 60-63): 4-byte addressing enter/exit methods.
    setUint32LE(bfpt, 60, (((enter4ByteMethods & 0xFF) << 24) | ((exit4ByteMethods & 0x3FF) << 14)) >>> 0);

    const length = fourByteTable ? Math.max(256, fourByteTablePointer + 8) : 256;
    const sfdp = new Uint8Array(length).fill(0xFF);
    sfdp[0] = 0x53; sfdp[1] = 0x46; sfdp[2] = 0x44; sfdp[3] = 0x50;  // "SFDP"
    sfdp[4] = minorRev;
    sfdp[5] = majorRev;
    sfdp[6] = fourByteTable ? 1 : 0;  // NPH: number of parameter headers - 1
    sfdp[7] = 0xFF;

    sfdp[8] = 0x00;      // BFPT parameter ID LSB
    sfdp[9] = minorRev;
    sfdp[10] = majorRev;
    sfdp[11] = bfptDwords;
    sfdp[12] = 0x30;     // table pointer (24-bit LE)
    sfdp[13] = 0x00;
    sfdp[14] = 0x00;
    sfdp[15] = 0xFF;     // BFPT parameter ID MSB

    sfdp.set(bfpt, 0x30);

    if (fourByteTable) {
        // Second parameter header: 4-byte Address Instruction Table (0xFF84)
        sfdp.set([0x84, 0x00, 0x01, 0x02,
            fourByteTablePointer & 0xFF, (fourByteTablePointer >> 8) & 0xFF,
            (fourByteTablePointer >> 16) & 0xFF, 0xFF], 16);

        let dword1 = 0;
        let dword2 = 0xFFFFFFFF;
        if (fourByteTable.read) dword1 |= 1 << 0;
        if (fourByteTable.program) dword1 |= 1 << 6;
        for (const [type, opcode] of Object.entries(fourByteTable.eraseOpcodes ?? {})) {
            const i = Number(type) - 1;
            dword1 |= 1 << (9 + i);
            dword2 = (dword2 & ~(0xFF << (8 * i))) | (opcode << (8 * i));
        }
        setUint32LE(sfdp, fourByteTablePointer, dword1 >>> 0);
        setUint32LE(sfdp, fourByteTablePointer + 4, dword2 >>> 0);
    }
    return sfdp;
}

/**
 * Captured raw SFDP blob from a real Winbond W25Q64 (8 MB, 3-byte addr),
 * stripped of the 5-byte SPI command echo. Used both as a DemoAdapter
 * fixture and as a known-good input for SFDP.js tests.
 */
const WINBOND_W25Q64_SFDP = hex2buf(
    "53464450050100ff" +
    "00050110800000ff" +
    "ff".repeat(112) +
    "e520f9ffffffff03" +
    "44eb086b083b42bb" +
    "feffffffffff0000" +
    "ffff40eb0c200f52" +
    "10d800003602a600" +
    "82ea14c4e9637633" +
    "7a757a75f7a2d55c" +
    "19f74dffe930f880" +
    "ff".repeat(64)
);

export { buildMinimalSFDP, WINBOND_W25Q64_SFDP };
